import React, { useState } from "react";
import { useGetSocietyLogsQuery } from "../../redux/societyApi";
import { useDebounce } from "use-debounce";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { IoClose } from "react-icons/io5";

const ACTION_TYPES = [
  { label: "All", value: "" },
  { label: "Create", value: "create" },
  { label: "Update", value: "update" },
  { label: "Delete", value: "delete" },
];

const ACTION_STYLES = {
  create: "bg-green-100 text-green-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-red-100 text-red-700",
};

const CHANGE_LABELS = {
  name: "Name",
  location: "Location",
  status: "Status",
  statusDescription: "Status Description",
  mortagedPlotsLink: "Mortaged Plots Link",
  map: "Map Image",
  onePager: "One Pager",
};

const FILE_FIELDS = ["map", "onePager"];

const getFileChangeLabel = (key, oldData, newData) => {
  const hadBefore = !!oldData?.[key];
  const hasAfter = !!newData?.[key];
  if (!hadBefore && hasAfter) return "Added";
  if (hadBefore && !hasAfter) return "Removed";
  return "Replaced";
};

const LogDetailModal = ({ log, onClose }) => {
  if (!log) return null;

  const changedFields = log.changes
    ? Object.entries(log.changes)
        .filter(([, changed]) => changed)
        .map(([key]) => key)
    : [];

  const unchangedFields = log.changes
    ? Object.entries(log.changes)
        .filter(([, changed]) => !changed)
        .map(([key]) => key)
    : [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-[#05004E] font-semibold text-xl">
                {log.newData?.name || log.oldData?.name || "—"}
              </h2>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                  ACTION_STYLES[log.action] || "bg-gray-100 text-gray-600"
                }`}
              >
                {log.action}
              </span>
            </div>
            <p className="text-gray-400 text-sm mt-1">
              {log.newData?.location || log.oldData?.location || ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <IoClose className="size-6" />
          </button>
        </div>

        {/* Meta info */}
        <div className="flex flex-wrap gap-6 px-6 py-4 bg-[#00643b08] border-b border-gray-100 text-sm">
          <div>
            <p className="text-gray-400 text-xs mb-0.5">Changed By</p>
            <p className="font-medium text-[#05004E]">
              {log.changedBy?.name || "—"}
            </p>
            {log.changedBy?.role && (
              <p className="text-xs text-gray-400 capitalize">
                {log.changedBy.role}
              </p>
            )}
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-0.5">Date</p>
            <p className="font-medium text-[#05004E]">
              {new Date(log.createdAt).toLocaleDateString("en-PK", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </p>
            <p className="text-xs text-gray-400">
              {new Date(log.createdAt).toLocaleTimeString("en-PK", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
        </div>

        <div className="p-6 flex flex-col gap-6">
          {/* Changed Fields */}
          {changedFields.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-[#05004E] mb-3">
                Changed Fields
                <span className="ml-2 text-xs font-normal bg-[#00643b15] text-[#00643b] px-2 py-0.5 rounded-full">
                  {changedFields.length}
                </span>
              </p>
              <div className="flex flex-col gap-3">
                {changedFields.map((key) => (
                  <div
                    key={key}
                    className="rounded-xl border border-[#00643b30] overflow-hidden"
                  >
                    <div
                      className="bg-[#00643b10] px-4 py-2 text-xs font-semibold text-[#00643b] uppercase
  tracking-wide"
                    >
                      {CHANGE_LABELS[key] || key}
                    </div>

                    {FILE_FIELDS.includes(key) ? (
                      // File fields — show intent label
                      <div className="px-4 py-3 text-sm text-gray-600">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            getFileChangeLabel(
                              key,
                              log.oldData,
                              log.newData,
                            ) === "Added"
                              ? "bg-green-100 text-green-700"
                              : getFileChangeLabel(
                                    key,
                                    log.oldData,
                                    log.newData,
                                  ) === "Removed"
                                ? "bg-red-100 text-red-700"
                                : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {getFileChangeLabel(key, log.oldData, log.newData)}
                        </span>
                      </div>
                    ) : (
                      // Scalar fields — show old vs new
                      <div className="grid grid-cols-2 divide-x divide-[#00643b20]">
                        <div className="px-4 py-3">
                          <p className="text-xs text-gray-400 mb-1">Before</p>
                          <p className="text-sm text-gray-500 break-words">
                            {log.oldData?.[key] || (
                              <span className="italic text-gray-300">
                                Empty
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="px-4 py-3">
                          <p className="text-xs text-gray-400 mb-1">After</p>
                          <p className="text-sm text-[#05004E] font-medium break-words">
                            {log.newData?.[key] || (
                              <span className="italic text-gray-300">
                                Empty
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unchanged Fields */}
          {unchangedFields.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-400 mb-3">
                Unchanged Fields
                <span className="ml-2 text-xs font-normal bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">
                  {unchangedFields.length}
                </span>
              </p>
              <div className="flex flex-col gap-2">
                {unchangedFields.map((key) => (
                  <div
                    key={key}
                    className="flex justify-between items-center px-4 py-3 rounded-xl bg-gray-50 text-sm"
                  >
                    <span className="text-gray-400">
                      {CHANGE_LABELS[key] || key}
                    </span>
                    <span className="text-gray-500 max-w-[60%] text-right break-words">
                      {FILE_FIELDS.includes(key)
                        ? log.newData?.[key]
                          ? "Has file"
                          : "No file"
                        : log.newData?.[key] || (
                            <span className="italic text-gray-300">Empty</span>
                          )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const HousingSocietiesLogs = () => {
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);

  const [debouncedSearch] = useDebounce(search, 1500);

  const { data, isLoading, isFetching } = useGetSocietyLogsQuery({
    page,
    type,
    search: debouncedSearch,
  });

  const logs = data?.societyLogs || [];

  const handlePrev = () => {
    if (page > 1) setPage((prev) => prev - 1);
  };

  const handleNext = () => {
    if (page < data?.totalPages) setPage((prev) => prev + 1);
  };

  return (
    <div className="flex flex-col gap-4 bg-white rounded-xl xl:p-8 lg:p-6 p-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-[#05004E] font-semibold lg:text-[1.8rem] md:text-[1.4rem] text-[1.2rem]">
          Housing Societies Logs
        </p>
        <p className="text-gray-400 text-sm">
          {data?.totalSocietyLogs ?? 0} total records
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap justify-between gap-3">
        {/* Type tabs */}
        <div className="flex gap-2 flex-wrap">
          {ACTION_TYPES.map((t) => (
            <button
              key={t.value}
              onClick={() => {
                setType(t.value);
                setPage(1);
              }}
              className={`px-4 py-2 rounded-xl text-sm font-medium border-2 transition-all ${
                type === t.value
                  ? "bg-[#00643b] text-white border-[#00643b]"
                  : "border-[#00643b40] text-[#00643b] hover:bg-[#00643b10]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <input
          type="search"
          placeholder="Search by name, location, or status..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="border-2 rounded-xl border-[#05004e40] md:p-3 p-2 outline-none md:w-[24rem] md:text-[1rem]
  text-[.8rem] w-[18rem]"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-[#00643b20]">
        <table className="w-full text-sm text-left">
          <thead className="bg-[#00643b] text-white">
            <tr>
              <th className="px-4 py-3 font-medium">Society</th>
              <th className="px-4 py-3 font-medium">Action</th>
              <th className="px-4 py-3 font-medium">Changed Fields</th>
              <th className="px-4 py-3 font-medium">Changed By</th>
              <th className="px-4 py-3 font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <tr>
                <td colSpan={5} className="text-center py-10 text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-10 text-gray-400">
                  No logs found
                </td>
              </tr>
            ) : (
              logs.map((log, index) => (
                <tr
                  key={log._id}
                  onClick={() => setSelectedLog(log)}
                  className={`border-t border-[#05004e10] ${
                    index % 2 === 0 ? "bg-white" : "bg-[#05004e05]"
                  }`}
                >
                  {/* Society name — prefer newData, fall back to oldData */}
                  <td className="px-4 py-3 font-medium text-[#05004E]">
                    {log.newData?.name || log.oldData?.name || "—"}
                    {log.newData?.location && (
                      <p className="text-xs text-gray-400 font-normal">
                        {log.newData.location}
                      </p>
                    )}
                  </td>

                  {/* Action badge */}
                  <td className="px-4 py-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                        ACTION_STYLES[log.action] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {log.action}
                    </span>
                  </td>

                  {/* Changed fields */}
                  <td className="px-4 py-3">
                    {log.changes && Object.keys(log.changes).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(log.changes)
                          .filter(([, changed]) => changed)
                          .map(([key]) => (
                            <span
                              key={key}
                              className="bg-[#05004e15] text-[#05004E] text-xs px-2 py-0.5 rounded-lg"
                            >
                              {CHANGE_LABELS[key] || key}
                            </span>
                          ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>

                  {/* Changed by */}
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#05004E]">
                      {log.changedBy?.name || "—"}
                    </p>
                    {log.changedBy?.role && (
                      <p className="text-xs text-gray-400 capitalize">
                        {log.changedBy.role}
                      </p>
                    )}
                  </td>

                  {/* Date */}
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {new Date(log.createdAt).toLocaleDateString("en-PK", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                    <p className="text-xs text-gray-400">
                      {new Date(log.createdAt).toLocaleTimeString("en-PK", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className=" flex justify-between items-center">
        <div className=" text-[#0000004f]">
          Showing record page {data?.currentPage} 0f {data?.totalPages}
        </div>
        <div className=" flex items-center gap-4">
          <div
            onClick={handlePrev}
            disabled={page === 1}
            className={`bg-[#02643B1F] p-2 text-[#00643b] rounded-xl ${
              page === 1 ? " cursor-not-allowed opacity-50" : "cursor-pointer"
            }`}
          >
            <IoIosArrowBack className="size-6" />
          </div>
          {data?.currentPage}
          <div
            onClick={handleNext}
            disabled={page === data?.totalPages}
            className={`bg-[#02643B1F] p-2 text-[#00643b] rounded-xl ${
              page === data?.totalPages
                ? " cursor-not-allowed opacity-50"
                : "cursor-pointer"
            }`}
          >
            <IoIosArrowForward className="size-6" />
          </div>
        </div>
      </div>

      <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
    </div>
  );
};

export default HousingSocietiesLogs;
